/**
 * @file: The events of the grid, from the server.
 *
 * The grid fetches the intervals of the week on show. A push from the server
 * starts a new fetch, but only for a change that can touch this scene. Each
 * fetch gets a new generation number, and each event gets the number of its
 * fetch. FullCalendar gives every event a new internal id on a fetch. Thus the
 * number tells a gesture if the events it holds are still in the grid.
 *
 * CalendarManager owns one instance. The manager owns the calendar and the
 * refetch timer, because a save and a gesture also ask for a refetch.
 */

import moment from 'moment'

/**
 * The updates that always fetch the week again. A change of a playlist or of
 * its scene link can change the set of collections, and the fetch builds the
 * id set again. A change can come while the socket is down, and nothing sends
 * it again after the reconnect.
 */
const ALWAYS_REFETCH = Object.freeze(['sceneCollection', 'sceneToCollection', 'reconnected'])

export class EventFeed {
  constructor (calendarManager, SceneCollectionService) {
    this.calendarManager = calendarManager
    this.controller = calendarManager.controller
    this.SceneCollectionService = SceneCollectionService
    this.calendarUnsubscribe = null
    // FullCalendar assigns new internal event ids on every fetch. A revert()
    // of an event from an earlier fetch adds that event as a second bar.
    // The first number counts the fetches that FullCalendar started. The second
    // number is the generation of the events in the grid.
    this.requestedFetchGeneration = 0
    this.eventFetchGeneration = 0
    // Stays null until the first fetch completes.
    this.sceneCollectionIds = null
  }

  setupWebSocketSubscriptions () {
    if (this.calendarUnsubscribe) {
      return
    }

    this.controller._$log.info(`[ScheduleCalendar] Setting up WebSocket subscriptions for scene ${this.controller.scene.id}`)

    this.calendarUnsubscribe = this.SceneCollectionService.subscribeToCalendarUpdates(
      this.controller.scene.id,
      (error, update) => {
        if (!error && this.calendarManager.calendar) {
          this.handleRealTimeUpdate(update)
        }
      }
    )
  }

  handleRealTimeUpdate (update) {
    if (!this.shouldRefetchFor(update)) {
      this.controller._$log.info('[ScheduleCalendar] Ignoring real-time update for another scene:', update.type)
      return
    }

    this.controller._$log.info('[ScheduleCalendar] Received real-time update:', update.type)

    // The delay gives the backend time to commit the change.
    this.calendarManager.scheduleRefetch(100)

    // A save that the panel sent on the old connection gets no answer.
    if (update.type === 'reconnected') {
      this.controller.followReconnect()
    }
  }

  /**
   * The subscriptions are system-wide, so the interval changes of all scenes
   * come here.
   *
   * An update with no scene collection is treated as an update for this scene.
   * A deletion sends only { id, removed }. If this method refuses that update,
   * the calendar keeps an event that does not exist.
   */
  shouldRefetchFor (update) {
    if (!update) {
      return false
    }

    if (ALWAYS_REFETCH.includes(update.type)) {
      return true
    }

    if (update.type !== 'interval') {
      return false
    }

    const sceneCollectionId = update.data && update.data.sceneCollectionId
    if (!sceneCollectionId || !this.sceneCollectionIds) {
      return true
    }

    return this.sceneCollectionIds.has(sceneCollectionId)
  }

  loadCalendarEvents (start, end) {
    if (!this.controller.scene || !this.controller.scene.id) {
      this.controller._$log.warn('[ScheduleCalendar] No scene ID available')
      return Promise.resolve([])
    }

    // The window is ISO 8601 with the offset. The server can run in another time zone.
    const startDate = moment(start).format()
    const endDate = moment(end).format()

    this.controller._$log.info(`[ScheduleCalendar] Loading events for scene ${this.controller.scene.id} from ${startDate} to ${endDate}`)

    return this.SceneCollectionService.getCalendarEvents(this.controller.scene.id, startDate, endDate)
      .then(sceneCollections => {
        // The response includes the collections with no intervals in this
        // window. This id set is therefore the full set for the scene.
        this.sceneCollectionIds = new Set(sceneCollections.map(sceneCollection => sceneCollection.id))

        const events = this.SceneCollectionService.formatIntervalsForCalendar(sceneCollections)
        return events
      })
  }

  /**
   * Tell if an event comes from the last fetch that the grid received.
   *
   * FullCalendar keeps copies of the events during a gesture. A copy from an
   * earlier fetch has internal ids that the grid no longer holds.
   *
   * @param {Object} event - A FullCalendar event, or a copy of one
   * @returns {boolean} False for an event from an earlier fetch
   */
  isFromLatestFetch (event) {
    return event.extendedProps.fetchGeneration === this.eventFetchGeneration
  }

  /**
   * Give the one source of the events of the grid.
   *
   * A failed fetch goes to FullCalendar and to the operator. An empty list
   * instead draws an empty festival week with no message. The grid then shows
   * a schedule that is not the stored schedule, and nothing tells the
   * operator.
   *
   * @returns {Object} An event source for FullCalendar
   */
  eventSource () {
    return {
      events: (fetchInfo, successCallback, failureCallback) => {
        const fetchGeneration = ++this.requestedFetchGeneration

        this.loadCalendarEvents(fetchInfo.start, fetchInfo.end)
          .then(events => {
            this.receiveGeneration(fetchGeneration)
            successCallback(events.map(event => ({
              ...event,
              extendedProps: { ...event.extendedProps, fetchGeneration }
            })))
          })
          .catch(error => {
            this.controller._$log.error('[ScheduleCalendar] Failed to load events:', error)

            this.controller._toaster.error(
              'The week on screen may be incomplete. Check the connection and reload.',
              'Could not load the schedule')

            failureCallback(error)
          })
      }
    }
  }

  /**
   * Record the generation of a fetch result that the grid keeps.
   *
   * FullCalendar keeps only the result of the last fetch that it started. It
   * drops the result of an earlier fetch that comes later (receiveRawEvents
   * compares the fetchId with latestFetchId). This method uses the same rule,
   * so a late result does not change the generation of the grid.
   */
  receiveGeneration (fetchGeneration) {
    if (fetchGeneration === this.requestedFetchGeneration) {
      this.eventFetchGeneration = fetchGeneration
    }
  }

  /** Remove the subscription. The page leaves, and the feed goes with it. */
  destroy () {
    if (this.calendarUnsubscribe && typeof this.calendarUnsubscribe === 'function') {
      this.calendarUnsubscribe()
      this.calendarUnsubscribe = null
    }
  }
}
